import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User as UserIcon, Shield, UserPlus, Search, Filter, 
  Edit, Trash2, Eye, Lock, Unlock, RefreshCw,
  AlertCircle, ChevronLeft, ChevronRight, MoreVertical,
  Key, Mail, Calendar, UserCheck, UserX, BarChart3,
  Download, Copy, CheckCircle, XCircle,
  TrendingUp, Users, Clock, Activity,
  ShieldCheck, KeyRound, Settings, LogOut
} from 'lucide-react';
import { AdminAuthAPI, AdminAPIUtils } from '../services/api';

function Admins() {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [actionMenu, setActionMenu] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchAdmins();
  }, [pagination.page]);

  const fetchStats = async () => {
    try {
      const data = await AdminAuthAPI.getAdminStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch admin stats:', err);
    }
  };

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await AdminAuthAPI.getAdmins(params);
      setAdmins(response.admins || []);
      setPagination({
        page: response.pagination?.page || 1,
        limit: response.pagination?.limit || 20,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.pages || 1
      });
    } catch (err) {
      const errorMsg = AdminAPIUtils.handleError(err, false).message;
      setError(errorMsg);
      if (err.status === 403) {
        // Redirect if not super admin
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchAdmins();
  };

  const getRoleBadge = (role) => {
    const config = {
      super_admin: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20', icon: <Shield className="w-3 h-3" /> },
      admin: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', icon: <UserCheck className="w-3 h-3" /> },
      support: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', icon: <Settings className="w-3 h-3" /> },
      finance: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', icon: <BarChart3 className="w-3 h-3" /> }
    };
    
    const configItem = config[role] || { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20', icon: <UserIcon className="w-3 h-3" /> };
    
    return (
      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${configItem.bg} ${configItem.text} border ${configItem.border} flex items-center gap-1.5`}>
        {configItem.icon}
        {role?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    if (status === 1) {
      return (
        <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1.5">
          <UserCheck className="w-3 h-3" />
          Active
        </span>
      );
    } else if (status === 2) {
      return (
        <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/10 text-rose-400 border border-rose-500/20 flex items-center gap-1.5">
          <Lock className="w-3 h-3" />
          Banned
        </span>
      );
    }
    return (
      <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-500/10 text-gray-400 border border-gray-500/20 flex items-center gap-1.5">
        <UserX className="w-3 h-3" />
        Inactive
      </span>
    );
  };

  const handleCreateAdmin = async (adminData) => {
    try {
      await AdminAuthAPI.createAdmin(adminData);
      setShowCreateModal(false);
      fetchAdmins();
      alert('Admin created successfully!');
    } catch (err) {
      AdminAPIUtils.handleError(err);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!selectedAdmin) return;
    
    try {
      await AdminAuthAPI.deleteAdmin(selectedAdmin.id);
      setShowDeleteModal(false);
      setSelectedAdmin(null);
      fetchAdmins();
      alert('Admin deleted successfully!');
    } catch (err) {
      AdminAPIUtils.handleError(err);
    }
  };

  const resetAdminPassword = async (adminId) => {
    const newPassword = prompt('Enter new password for this admin (min 8 characters):');
    if (!newPassword || newPassword.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }
    
    if (!window.confirm('Are you sure you want to reset this admin\'s password?')) {
      return;
    }
    
    try {
      await AdminAuthAPI.changeAdminPassword(adminId, newPassword);
      alert('Password reset successfully!');
    } catch (err) {
      AdminAPIUtils.handleError(err);
    }
  };

  const statCards = [
    {
      title: 'Total Admins',
      value: stats?.total_admins || 0,
      icon: <Users className="w-5 h-5" />,
      color: 'from-blue-500 to-cyan-500',
      change: '2',
      changeText: 'new this month',
      trend: 'up'
    },
    {
      title: 'Super Admins',
      value: stats?.admins_by_role?.find(r => r.role === 'super_admin')?.count || 0,
      icon: <ShieldCheck className="w-5 h-5" />,
      color: 'from-purple-500 to-violet-500',
      change: 'Full access',
      changeText: 'privileges',
      trend: 'neutral'
    },
    {
      title: 'Recent Logins',
      value: stats?.recent_logins?.filter(l => l.success).length || 0,
      icon: <Activity className="w-5 h-5" />,
      color: 'from-emerald-500 to-green-500',
      change: '24h',
      changeText: 'last activity',
      trend: 'up'
    },
    {
      title: 'Failed Logins',
      value: stats?.recent_logins?.filter(l => !l.success).length || 0,
      icon: <AlertCircle className="w-5 h-5" />,
      color: 'from-rose-500 to-red-500',
      change: 'Attempts',
      changeText: 'in last 10',
      trend: 'neutral'
    },
  ];

  if (!AdminAuthAPI.isSuperAdmin()) {
    return (
      <div className="flex justify-center items-center h-screen bg-deep-black">
        <div className="text-center">
          <Shield className="w-20 h-20 text-rose-400 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
          <p className="text-charcoal-gray mb-8 max-w-md">You need super admin privileges to access this page.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-105"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
            Admin Management
          </h1>
          <p className="text-charcoal-gray mt-1">Manage administrator accounts and permissions</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
          >
            <UserPlus className="w-4 h-4" />
            Create Admin
          </button>
          <button
            onClick={fetchAdmins}
            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl flex items-center gap-2 transition-all duration-300 hover:scale-[1.02]"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <div 
            key={index} 
            className="bg-night-blue/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:bg-night-blue/70 transition-all duration-300 hover:scale-[1.02] group"
          >
            <div className="flex items-start justify-between mb-6">
              <div className={`p-3 rounded-xl bg-gradient-to-br ${card.color} shadow-lg`}>
                {card.icon}
              </div>
              {card.trend === 'up' && (
                <div className="flex items-center text-emerald-400 bg-emerald-400/10 px-3 py-1 rounded-full">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">+{card.change}</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-3xl font-bold text-white">{card.value}</p>
              <p className="text-charcoal-gray text-sm uppercase tracking-wider">{card.title}</p>
            </div>
            <div className="mt-6 pt-4 border-t border-gray-800">
              <p className="text-sm text-charcoal-gray">
                <span className={`font-medium ${card.trend === 'up' ? 'text-emerald-400' : card.trend === 'down' ? 'text-rose-400' : 'text-gray-400'}`}>
                  {card.trend === 'up' ? '+' : ''}{card.change}
                </span>{' '}
                {card.changeText}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Search Bar */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl p-6">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-charcoal-gray w-5 h-5" />
              <input
                type="text"
                placeholder="Search by username, email, or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white placeholder-charcoal-gray focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all duration-300"
              />
            </div>
          </div>
          <button
            type="submit"
            className="px-5 py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 hover:scale-[1.02]"
          >
            Search
          </button>
        </form>
      </div>

      {/* Admins Table */}
      <div className="bg-night-blue/30 backdrop-blur-sm border border-gray-800 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-sky-500 mx-auto mb-6"></div>
              <p className="text-charcoal-gray">Loading admins...</p>
            </div>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-rose-400 mx-auto mb-4" />
            <div className="text-rose-300 mb-4 font-medium">Error: {error}</div>
            <button
              onClick={fetchAdmins}
              className="px-5 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300"
            >
              Retry
            </button>
          </div>
        ) : admins.length === 0 ? (
          <div className="p-8 text-center">
            <UserIcon className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No admins found</h3>
            <p className="text-charcoal-gray mb-6">Create your first admin account</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl transition-all duration-300"
            >
              <UserPlus className="w-4 h-4 inline-block mr-2" />
              Create Admin
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-800">
                <thead className="bg-gray-900/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <UserIcon className="w-4 h-4" />
                        Admin
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Role
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Activity className="w-4 h-4" />
                        Status
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Created
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Last Login
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-charcoal-gray uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {admins.map((admin) => (
                    <tr key={admin.id} className="hover:bg-gray-800/30 transition-colors group">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="relative">
                            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                              {admin.username?.charAt(0).toUpperCase() || 'A'}
                            </div>
                            <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-night-blue ${
                              admin.status === 1 ? 'bg-emerald-500' : 
                              admin.status === 2 ? 'bg-rose-500' : 
                              'bg-gray-500'
                            }`} />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white group-hover:text-sky-300 transition-colors">
                              {admin.username}
                            </div>
                            <div className="text-sm text-charcoal-gray truncate max-w-[160px]">{admin.email}</div>
                            {admin.full_name && (
                              <div className="text-xs text-gray-500">{admin.full_name}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getRoleBadge(admin.role)}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(admin.status)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="text-sm text-white">
                            {AdminAPIUtils.formatDate(admin.created_at, 'date')}
                          </div>
                          {admin.created_by_username && (
                            <div className="text-xs text-charcoal-gray">By: {admin.created_by_username}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="text-sm text-white">
                            {admin.last_login_at ? AdminAPIUtils.formatDate(admin.last_login_at, 'datetime') : 'Never'}
                          </div>
                          <div className="text-xs text-charcoal-gray">
                            {admin.last_login_at ? AdminAPIUtils.timeAgo(admin.last_login_at) : ''}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => navigate(`/admins/${admin.id}`)}
                            className="p-2 bg-gray-800/50 hover:bg-sky-500/20 text-sky-400 hover:text-sky-300 rounded-lg transition-all duration-300 hover:scale-110"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          
                          <button
                            onClick={() => navigate(`/admins/${admin.id}/edit`)}
                            className="p-2 bg-gray-800/50 hover:bg-amber-500/20 text-amber-400 hover:text-amber-300 rounded-lg transition-all duration-300 hover:scale-110"
                            title="Edit Admin"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          
                          <button
                            onClick={() => resetAdminPassword(admin.id)}
                            className="p-2 bg-gray-800/50 hover:bg-emerald-500/20 text-emerald-400 hover:text-emerald-300 rounded-lg transition-all duration-300 hover:scale-110"
                            title="Reset Password"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          
                          {admin.role !== 'super_admin' && (
                            <button
                              onClick={() => {
                                setSelectedAdmin(admin);
                                setShowDeleteModal(true);
                              }}
                              className="p-2 bg-gray-800/50 hover:bg-rose-500/20 text-rose-400 hover:text-rose-300 rounded-lg transition-all duration-300 hover:scale-110"
                              title="Delete Admin"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          
                          <div className="relative">
                            <button
                              onClick={() => setActionMenu(actionMenu === admin.id ? null : admin.id)}
                              className="p-2 bg-gray-800/50 hover:bg-gray-700 text-gray-400 hover:text-white rounded-lg transition-all duration-300 hover:scale-110"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                            {actionMenu === admin.id && (
                              <div className="absolute right-0 mt-2 w-56 bg-night-blue border border-gray-800 rounded-xl shadow-2xl py-2 z-50">
                                <div className="px-4 py-2 border-b border-gray-800">
                                  <p className="text-sm font-medium text-white">Admin Actions</p>
                                </div>
                                <button
                                  onClick={() => {
                                    const email = prompt('Enter new email for this admin:', admin.email);
                                    if (email && email !== admin.email) {
                                      AdminAuthAPI.updateAdmin(admin.id, { email })
                                        .then(() => {
                                          alert('Email updated successfully');
                                          fetchAdmins();
                                        })
                                        .catch(AdminAPIUtils.handleError);
                                    }
                                    setActionMenu(null);
                                  }}
                                  className="w-full px-4 py-2.5 text-sm text-blue-400 hover:bg-blue-500/10 flex items-center gap-2 transition-colors"
                                >
                                  <Mail className="w-4 h-4" />
                                  Change Email
                                </button>
                                <button
                                  onClick={() => {
                                    const status = admin.status === 1 ? 0 : 1;
                                    const action = status === 1 ? 'activate' : 'deactivate';
                                    if (window.confirm(`Are you sure you want to ${action} this admin?`)) {
                                      AdminAuthAPI.updateAdmin(admin.id, { status })
                                        .then(() => {
                                          alert(`Admin ${action}d successfully`);
                                          fetchAdmins();
                                        })
                                        .catch(AdminAPIUtils.handleError);
                                    }
                                    setActionMenu(null);
                                  }}
                                  className="w-full px-4 py-2.5 text-sm text-amber-400 hover:bg-amber-500/10 flex items-center gap-2 transition-colors"
                                >
                                  {admin.status === 1 ? (
                                    <>
                                      <UserX className="w-4 h-4" />
                                      Deactivate
                                    </>
                                  ) : (
                                    <>
                                      <UserCheck className="w-4 h-4" />
                                      Activate
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={() => navigate(`/logs?admin_id=${admin.id}`)}
                                  className="w-full px-4 py-2.5 text-sm text-purple-400 hover:bg-purple-500/10 flex items-center gap-2 transition-colors"
                                >
                                  <Activity className="w-4 h-4" />
                                  View Activity Logs
                                </button>
                                <div className="px-4 py-2 border-t border-gray-800">
                                  <p className="text-xs text-charcoal-gray">Admin ID: {admin.id}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-800">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-charcoal-gray">
                    Page <span className="font-medium text-white">{pagination.page}</span> of{' '}
                    <span className="font-medium text-white">{pagination.totalPages}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={pagination.page === 1}
                      className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 ${
                        pagination.page === 1
                          ? 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                      }`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </button>
                    <div className="flex items-center space-x-1">
                      {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                            className={`px-4 py-2.5 min-w-[40px] rounded-xl transition-all duration-300 ${
                              pagination.page === pageNum
                                ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg'
                                : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      {pagination.totalPages > 5 && pagination.page < pagination.totalPages - 2 && (
                        <>
                          <span className="px-2 text-charcoal-gray">...</span>
                          <button
                            onClick={() => setPagination(prev => ({ ...prev, page: pagination.totalPages }))}
                            className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl hover:scale-105 transition-all duration-300"
                          >
                            {pagination.totalPages}
                          </button>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.totalPages, prev.page + 1) }))}
                      disabled={pagination.page === pagination.totalPages}
                      className={`px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all duration-300 ${
                        pagination.page === pagination.totalPages
                          ? 'bg-gray-800/50 text-gray-600 cursor-not-allowed'
                          : 'bg-gray-800 hover:bg-gray-700 text-white hover:scale-105'
                      }`}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Admin Modal */}
      {showCreateModal && (
        <CreateAdminModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateAdmin}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedAdmin && (
        <DeleteConfirmationModal
          admin={selectedAdmin}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedAdmin(null);
          }}
          onConfirm={handleDeleteAdmin}
        />
      )}
    </div>
  );
}

// Create Admin Modal Component
function CreateAdminModal({ onClose, onCreate }) {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    full_name: '',
    role: 'admin'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      await onCreate({
        username: formData.username,
        password: formData.password,
        email: formData.email,
        full_name: formData.full_name || null,
        role: formData.role
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-night-blue border border-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-lg font-bold text-white">Create New Admin</h3>
              <p className="text-sm text-charcoal-gray mt-1">Add a new administrator account</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-1.5 hover:bg-gray-800/50 rounded-lg"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Username *
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-gray-900/50 border rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors ${
                  errors.username ? 'border-rose-500' : 'border-gray-800'
                }`}
                placeholder="Enter username"
              />
              {errors.username && (
                <p className="mt-1 text-sm text-rose-400">{errors.username}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-gray-900/50 border rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors ${
                  errors.email ? 'border-rose-500' : 'border-gray-800'
                }`}
                placeholder="admin@example.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-rose-400">{errors.email}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Full Name (Optional)
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
                placeholder="John Doe"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Role *
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-900/50 border border-gray-800 rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors"
              >
                <option value="admin">Admin</option>
                <option value="support">Support</option>
                <option value="finance">Finance</option>
              </select>
              <p className="mt-2 text-xs text-charcoal-gray">
                Super admin role cannot be assigned
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Password *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-gray-900/50 border rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors ${
                  errors.password ? 'border-rose-500' : 'border-gray-800'
                }`}
                placeholder="Minimum 8 characters"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-rose-400">{errors.password}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Confirm Password *
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-gray-900/50 border rounded-xl text-white focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-colors ${
                  errors.confirmPassword ? 'border-rose-500' : 'border-gray-800'
                }`}
                placeholder="Confirm password"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-rose-400">{errors.confirmPassword}</p>
              )}
            </div>
            
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-800">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-xl transition-all duration-300 hover:scale-105 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    Create Admin
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Delete Confirmation Modal Component
function DeleteConfirmationModal({ admin, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-night-blue border border-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-rose-500/10 mb-4">
              <AlertCircle className="h-8 w-8 text-rose-400" />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">
              Delete Admin Account
            </h3>
            <p className="text-charcoal-gray mb-6">
              Are you sure you want to delete the admin account <span className="font-semibold text-white">{admin.username}</span>?
              This action cannot be undone.
            </p>
            
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6 text-left">
              <h4 className="font-medium text-amber-400 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Important Note:
              </h4>
              <ul className="text-sm text-amber-300/80 space-y-1">
                <li className="flex items-start gap-2">
                  <span className="text-xs mt-1">•</span>
                  <span>This will permanently deactivate the admin account</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-xs mt-1">•</span>
                  <span>The admin will no longer be able to login</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-xs mt-1">•</span>
                  <span>Account data will be preserved for audit purposes</span>
                </li>
                {admin.role === 'super_admin' && (
                  <li className="flex items-start gap-2 font-bold text-rose-400">
                    <span className="text-xs mt-1">•</span>
                    <span>Super admin accounts cannot be deleted</span>
                  </li>
                )}
              </ul>
            </div>
            
            <div className="flex justify-center gap-3">
              <button
                onClick={onClose}
                className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-all duration-300"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={loading || admin.role === 'super_admin'}
                className={`px-5 py-2.5 rounded-xl transition-all duration-300 flex items-center gap-2 ${
                  admin.role === 'super_admin'
                    ? 'bg-gray-800/50 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white hover:scale-105'
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete Admin
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Admins;